import { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router';
import TableBody from '@material-ui/core/TableBody';
import TableHead from '@material-ui/core/TableHead';
import TableCellRaw from '@material-ui/core/TableCell';
import { Card, CardBody } from '@/shared/components/Card';
import { Table, TableRow, TableWrap, TableCell, Toolbar, TableButton } from '@/shared/components/MaterialTableElements';
import { LoadingSpinner } from '@/shared/components/Loading';
import { MaterialFormContainer, MaterialFormOption, MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { ScheduleQueueItemStatus } from '@/constants';
import { ScheduleQueueItem } from '@/models/schedule';
import {
    dangerButtonStyle,
    darkDangerText,
    darkInfoText,
    darkSuccessText,
    darkTextDim,
    iconButtonStyle,
    plainButtonStyle,
} from '@/shared/dark-inline-styles';
import {
    useDeleteQueueItem,
    useDeleteSchedule,
    usePublishNow,
    useQueueItems,
    useReorderQueue,
    useSchedule,
    useUpdateSchedule,
} from '@/services/schedules';
import QueueItemModal from './components/queue-item-modal';
import { extractErrorMessage, formatDate } from '@/utils/helpers';
import PlusIcon from 'mdi-react/PlusIcon';
import ArrowUpIcon from 'mdi-react/ArrowUpIcon';
import ArrowDownIcon from 'mdi-react/ArrowDownIcon';
import DeleteIcon from 'mdi-react/DeleteIcon';
import PencilIcon from 'mdi-react/PencilIcon';

/**
 * Status chips, as a TINT + a light label — never a light fill with white text.
 *
 * These used to be `background: <one of the status colours>, color: 'white'`, and
 * three of the four status colours are the LIGHTENED text tokens from
 * `dark-inline-styles` (#6ddba0, #ff9b9b) or a mid grey (#9e9e9e). White on those
 * measures between 1.8:1 and 2.4:1 — the label was there and could not be read,
 * which on a queue whose whole job is to say what happened is the one thing the
 * column must never do. The tokens are text colours; using one as a fill inverts
 * the contrast they were chosen for.
 *
 * Same shape as the banner tokens beside them: a translucent wash of the hue for
 * the fill, the light hue for the text, a matching border to hold the edge.
 */
const STATUS_CHIPS: Record<ScheduleQueueItemStatus, { color: string; background: string; border: string }> = {
    [ScheduleQueueItemStatus.Queued]: { color: darkInfoText, background: 'rgba(125, 163, 255, 0.14)', border: 'rgba(125, 163, 255, 0.35)' },
    [ScheduleQueueItemStatus.Published]: { color: darkSuccessText, background: 'rgba(109, 219, 160, 0.14)', border: 'rgba(109, 219, 160, 0.35)' },
    [ScheduleQueueItemStatus.Skipped]: { color: darkTextDim, background: 'rgba(255, 255, 255, 0.08)', border: 'rgba(255, 255, 255, 0.2)' },
    [ScheduleQueueItemStatus.Failed]: { color: darkDangerText, background: 'rgba(255, 107, 107, 0.14)', border: 'rgba(255, 107, 107, 0.35)' },
};

const previewPayload = (payload: Record<string, unknown>): string => {
    const title = payload.title as string | undefined;
    if (title) return title.length > 60 ? title.slice(0, 57) + '...' : title;
    const description = payload.description as string | undefined;
    if (description) return description.length > 60 ? description.slice(0, 57) + '...' : description;
    return '(payload set, no title/description)';
};

const ScheduleDetail = () => {
    const navigate = useNavigate();
    const { scheduleId } = useParams<{ scheduleId: string }>();
    const { data: scheduleResp, isLoading } = useSchedule(scheduleId);
    const { data: queueResp, isLoading: isQueueLoading } = useQueueItems(scheduleId);
    const { mutateAsync: updateSchedule, isPending: isUpdating } = useUpdateSchedule();
    const { mutateAsync: deleteSchedule, isPending: isDeleting } = useDeleteSchedule();
    const { mutateAsync: publishNow, isPending: isPublishing } = usePublishNow();
    const { mutateAsync: reorderQueue, isPending: isReordering } = useReorderQueue();
    const { mutateAsync: deleteQueueItem } = useDeleteQueueItem();

    const schedule = scheduleResp?.data;
    const items: ScheduleQueueItem[] = queueResp?.data ?? [];

    const [hour, setHour] = useState(0);
    const [minute, setMinute] = useState(0);
    const [enabled, setEnabled] = useState(true);
    const [name, setName] = useState('');
    const [savedNotice, setSavedNotice] = useState<string | null>(null);

    const [editorOpen, setEditorOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<ScheduleQueueItem | undefined>(undefined);

    useEffect(() => {
        if (schedule) {
            setHour(schedule.publishAtHourUtc);
            setMinute(schedule.publishAtMinuteUtc);
            setEnabled(schedule.enabled);
            setName(schedule.name);
        }
    }, [schedule?.id]);

    if (isLoading || !schedule) {
        return (
            <Container>
                <div className="d-flex justify-content-center pt-5">
                    <LoadingSpinner />
                </div>
            </Container>
        );
    }

    const handleSaveConfig = async () => {
        try {
            // callApi resolves { status:'error' } rather than throwing, so check it
            // or a failed save shows "Saved".
            const result = await updateSchedule({
                scheduleId: schedule.id,
                name: name.trim(),
                publishAtHourUtc: hour,
                publishAtMinuteUtc: minute,
                enabled,
            });
            if (result.status === 'error') {
                setSavedNotice(`Save failed: ${result.error?.message ?? 'unknown error'}`);
                return;
            }
            setSavedNotice('Saved');
            setTimeout(() => setSavedNotice(null), 2000);
        } catch (e) {
            console.error(e);
            setSavedNotice(`Save failed: ${extractErrorMessage(e)}`);
        }
    };

    const handleDelete = async () => {
        if (!confirm('Delete this schedule? This is a soft delete and the rotator will stop firing it.')) return;
        try {
            const result = await deleteSchedule({ scheduleId: schedule.id });
            if (result.status === 'error') {
                setSavedNotice(`Delete failed: ${result.error?.message ?? 'unknown error'}`);
                return;
            }
            navigate('/schedules');
        } catch (e) {
            console.error(e);
            setSavedNotice(`Delete failed: ${extractErrorMessage(e)}`);
        }
    };

    const handlePublishNow = async () => {
        if (!confirm('Publish the next queue item NOW? This consumes one queue item and advances the next scheduled fire to tomorrow.')) return;
        try {
            // A 409 raced-state (scheduleQueueItemRaced) resolves as { status:'error' },
            // not a throw — without this check it would falsely show "Published".
            const result = await publishNow({ scheduleId: schedule.id });
            if (result.status === 'error') {
                setSavedNotice(`Publish failed: ${result.error?.message ?? 'unknown error'}`);
                return;
            }
            setSavedNotice('Published');
            setTimeout(() => setSavedNotice(null), 2000);
        } catch (e) {
            console.error(e);
            setSavedNotice(`Publish failed: ${extractErrorMessage(e)}`);
        }
    };

    /**
     * Reorder is scoped to the currently-Queued items only. Published / Skipped /
     * Failed items keep their historical orderIndex untouched — overwriting
     * those would corrupt the audit trail and could promote already-failed
     * items above pending Queued ones if a future "retry" feature ever
     * rescues failed items.
     */
    const handleMove = async (queuedIdx: number, direction: -1 | 1) => {
        const queuedItems = items.filter((it) => it.status === ScheduleQueueItemStatus.Queued);
        const target = queuedIdx + direction;
        if (target < 0 || target >= queuedItems.length) return;
        const reordered = [...queuedItems];
        [reordered[queuedIdx], reordered[target]] = [reordered[target], reordered[queuedIdx]];
        try {
            const result = await reorderQueue({
                scheduleId: schedule.id,
                updates: reordered.map((it, i) => ({ itemId: it.id, orderIndex: i })),
            });
            if (result.status === 'error') {
                setSavedNotice(`Reorder failed: ${result.error?.message ?? 'unknown error'}`);
            }
        } catch (e) {
            console.error(e);
            setSavedNotice(`Reorder failed: ${extractErrorMessage(e)}`);
        }
    };

    const handleDeleteQueueItem = async (item: ScheduleQueueItem) => {
        if (!confirm(`Delete this queue item? This is a soft delete.`)) return;
        try {
            const result = await deleteQueueItem({ scheduleId: schedule.id, itemId: item.id });
            if (result.status === 'error') {
                setSavedNotice(`Delete failed: ${result.error?.message ?? 'unknown error'}`);
            }
        } catch (e) {
            console.error(e);
            setSavedNotice(`Delete failed: ${extractErrorMessage(e)}`);
        }
    };

    return (
        <Container>
            <Row>
                <Col md={12}>
                    <h3 className="page-title">{schedule.name}</h3>
                    <h3 className="page-subhead subhead">
                        {schedule.target.type} → {schedule.target.title ?? schedule.target.id}
                    </h3>
                </Col>
            </Row>

            {/* Config card */}
            <Row className="mt-2">
                <Col md={12}>
                    <Card>
                        <CardBody>
                            <h4>Schedule config</h4>
                            <MaterialFormContainer onSubmit={handleSaveConfig}>
                                <MaterialTextField label="Name" value={name} onChange={(e) => setName(e.target.value)} />
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <MaterialTextField
                                        label="Hour (UTC, 0-23)"
                                        type="number"
                                        value={hour}
                                        onChange={(e) => setHour(Number(e.target.value))}
                                    />
                                    <MaterialTextField
                                        label="Minute (UTC, 0-59)"
                                        type="number"
                                        value={minute}
                                        onChange={(e) => setMinute(Number(e.target.value))}
                                    />
                                </div>
                                <MaterialTextField
                                    name="select"
                                    select
                                    label="Enabled"
                                    value={enabled ? 'yes' : 'no'}
                                    onChange={(e) => setEnabled(e.target.value === 'yes')}
                                >
                                    <MaterialFormOption value="yes">Enabled</MaterialFormOption>
                                    <MaterialFormOption value="no">Disabled</MaterialFormOption>
                                </MaterialTextField>
                                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12 }}>
                                    <button type="button" onClick={handleSaveConfig} disabled={isUpdating} style={plainButtonStyle}>
                                        Save config
                                    </button>
                                    <button type="button" onClick={handlePublishNow} disabled={isPublishing} style={plainButtonStyle}>
                                        Publish NOW (consumes head of queue)
                                    </button>
                                    <button type="button" onClick={handleDelete} disabled={isDeleting} style={dangerButtonStyle}>
                                        Delete schedule
                                    </button>
                                    {savedNotice && <span style={{ color: '#66bb6a' }}>{savedNotice}</span>}
                                </div>
                                <div style={{ marginTop: 12, fontSize: 13, color: '#999999' }}>
                                    <div>Next publish: {schedule.nextPublishAt ? formatDate(schedule.nextPublishAt) : '—'}</div>
                                    <div>Last published: {schedule.lastPublishedAt ? formatDate(schedule.lastPublishedAt) : 'never'}</div>
                                    {schedule.lastPublishError && (
                                        <div style={{ color: darkDangerText }}>Last error: {schedule.lastPublishError}</div>
                                    )}
                                </div>
                            </MaterialFormContainer>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Queue */}
            <Row className="mt-3">
                <Col md={12}>
                    <Card>
                        <CardBody>
                            <div className="d-flex align-items-center justify-content-between">
                                <h4>Queue ({items.length})</h4>
                                <Toolbar className="material-table__toolbar">
                                    <div
                                        className="d-flex align-items-center justify-content-center"
                                        role="button"
                                        onClick={() => {
                                            setEditingItem(undefined);
                                            setEditorOpen(true);
                                        }}
                                    >
                                        <TableButton>
                                            <PlusIcon />
                                        </TableButton>
                                        <span>Add to queue</span>
                                    </div>
                                </Toolbar>
                            </div>
                            {isQueueLoading ? (
                                <LoadingSpinner />
                            ) : (
                                <TableWrap>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCellRaw padding="none">#</TableCellRaw>
                                                <TableCellRaw>Status</TableCellRaw>
                                                <TableCellRaw>Preview</TableCellRaw>
                                                <TableCellRaw>Created</TableCellRaw>
                                                <TableCellRaw>Published</TableCellRaw>
                                                <TableCellRaw>Actions</TableCellRaw>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(() => {
                                                // Build a queued-only index map so the move buttons reorder
                                                // by queue position (not absolute table position) — published
                                                // / failed / skipped items aren't touched by reorder.
                                                const queuedItems = items.filter((it) => it.status === ScheduleQueueItemStatus.Queued);
                                                const queuedPositionById = new Map(queuedItems.map((it, i) => [it.id, i]));
                                                return items.map((it, idx) => {
                                                    const queuedPos = queuedPositionById.get(it.id);
                                                    const isQueued = queuedPos !== undefined;
                                                    return (
                                                        <TableRow key={it.id} tabIndex={-1}>
                                                            <TableCell padding="none">{idx + 1}</TableCell>
                                                            <TableCell>
                                                                <span
                                                                    style={{
                                                                        ...STATUS_CHIPS[it.status],
                                                                        border: `1px solid ${STATUS_CHIPS[it.status].border}`,
                                                                        padding: '2px 8px',
                                                                        borderRadius: 4,
                                                                        fontSize: 12,
                                                                    }}
                                                                >
                                                                    {it.status}
                                                                </span>
                                                                {it.publishError && (
                                                                    <div style={{ color: darkDangerText, fontSize: 12 }}>{it.publishError}</div>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>{previewPayload(it.payload)}</TableCell>
                                                            <TableCell>{formatDate(it.created_at)}</TableCell>
                                                            <TableCell>{it.publishedAt ? formatDate(it.publishedAt) : '—'}</TableCell>
                                                            <TableCell>
                                                                <div style={{ display: 'flex', gap: 6 }}>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => isQueued && handleMove(queuedPos as number, -1)}
                                                                        disabled={!isQueued || queuedPos === 0 || isReordering}
                                                                        title={isQueued ? 'Move up' : 'Only queued items can be reordered'}
                                                                        style={iconButtonStyle}
                                                                    >
                                                                        <ArrowUpIcon size={14} />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => isQueued && handleMove(queuedPos as number, 1)}
                                                                        disabled={!isQueued || queuedPos === queuedItems.length - 1 || isReordering}
                                                                        title={isQueued ? 'Move down' : 'Only queued items can be reordered'}
                                                                        style={iconButtonStyle}
                                                                    >
                                                                        <ArrowDownIcon size={14} />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setEditingItem(it);
                                                                            setEditorOpen(true);
                                                                        }}
                                                                        title="Edit"
                                                                        style={iconButtonStyle}
                                                                    >
                                                                        <PencilIcon size={14} />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleDeleteQueueItem(it)}
                                                                        title="Delete"
                                                                        style={dangerButtonStyle}
                                                                    >
                                                                        <DeleteIcon size={14} />
                                                                    </button>
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                });
                                            })()}
                                            {items.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={6} className="text-center">
                                                        Queue is empty. Add an item — it&apos;ll publish at the next slot.
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableWrap>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <QueueItemModal isOpen={editorOpen} toggle={() => setEditorOpen((p) => !p)} schedule={schedule} existingItem={editingItem} />
        </Container>
    );
};

export default ScheduleDetail;
